import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import AvailabilityCalendar from '../../components/rental/AvailabilityCalendar';
import { 
  Calendar,
  ArrowLeft,
  Loader2,
  Check,
  X,
  Plus,
  Edit,
  Trash2,
  Eye,
  Phone,
  Mail,
  User,
  MapPin,
  DollarSign,
  Clock,
  FileText,
  List,
  CalendarDays
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: 'pending', label: 'En attente', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  { value: 'confirmed', label: 'Confirmé', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'active', label: 'Actif', color: 'bg-green-100 text-green-800 border-green-200' },
  { value: 'returned', label: 'Retourné', color: 'bg-gray-100 text-gray-600 border-gray-200' },
  { value: 'cancelled', label: 'Annulé', color: 'bg-red-100 text-red-800 border-red-200' }
];

const paymentStatusOptions = [
  { value: 'pending', label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'paid', label: 'Payé', color: 'bg-green-100 text-green-800' }
];

export default function AdminRentals() {
  const { token, isAdmin, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [rentals, setRentals] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingRental, setEditingRental] = useState(null);
  const [viewingRental, setViewingRental] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    product_id: '',
    start_date: '',
    end_date: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    notes: '',
    status: 'pending',
    payment_status: 'pending',
    delivery_address: {
      street: '',
      city: '',
      postal_code: '',
      province: 'QC'
    }
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isAdmin()) {
      navigate('/');
      return;
    }
    fetchRentals();
    fetchProducts();
  }, [isAuthenticated, authLoading, statusFilter]);

  const fetchRentals = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/admin/rentals${params}`, { headers });
      setRentals(response.data);
    } catch (error) {
      console.error('Error fetching rentals:', error);
      toast.error('Erreur lors du chargement des réservations');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/rentals/products/available`, { headers });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleCreate = () => {
    setEditingRental(null);
    setFormData({
      product_id: '',
      start_date: '',
      end_date: '',
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      notes: '',
      status: 'pending',
      payment_status: 'pending',
      delivery_address: {
        street: '',
        city: '',
        postal_code: '',
        province: 'QC'
      }
    });
    setIsModalOpen(true);
  };

  // Create reservation from calendar selection
  const handleCreateFromCalendar = (dates) => {
    setEditingRental(null);
    setFormData({
      product_id: '',
      start_date: dates.start,
      end_date: dates.end,
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      notes: '',
      status: 'pending',
      payment_status: 'pending',
      delivery_address: {
        street: '',
        city: '',
        postal_code: '',
        province: 'QC'
      }
    });
    setIsModalOpen(true);
  };

  const handleEdit = (rental) => {
    setEditingRental(rental);
    setFormData({
      product_id: rental.product_id,
      start_date: rental.start_date?.split('T')[0] || '',
      end_date: rental.end_date?.split('T')[0] || '',
      customer_name: rental.customer_name || '',
      customer_email: rental.customer_email || rental.user_email || '',
      customer_phone: rental.customer_phone || '',
      notes: rental.notes || '',
      status: rental.status,
      payment_status: rental.payment_status || 'pending',
      delivery_address: rental.delivery_address || {
        street: '',
        city: '',
        postal_code: '',
        province: 'QC'
      }
    });
    setIsModalOpen(true);
  };

  const handleView = async (rental) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/rentals/${rental.id}`, { headers });
      setViewingRental(response.data);
      setIsViewModalOpen(true);
    } catch (error) {
      console.error('Error fetching rental details:', error);
      toast.error('Erreur lors du chargement des détails');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const dataToSend = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
      };
      
      if (editingRental) {
        await axios.put(`${API}/admin/rentals/${editingRental.id}`, dataToSend, { headers });
        toast.success('Réservation modifiée avec succès');
      } else {
        await axios.post(`${API}/admin/rentals`, dataToSend, { headers });
        toast.success('Réservation créée avec succès');
      }
      
      setIsModalOpen(false);
      fetchRentals();
    } catch (error) {
      console.error('Error saving rental:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (rentalId, newStatus) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/rentals/${rentalId}/status?status=${newStatus}`, {}, { headers });
      toast.success('Statut mis à jour');
      fetchRentals();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (rentalId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/rentals/${rentalId}`, { headers });
      toast.success('Réservation supprimée');
      setDeleteConfirm(null);
      fetchRentals();
    } catch (error) {
      console.error('Error deleting rental:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const found = statusOptions.find(s => s.value === status);
    return <Badge className={`${found?.color || 'bg-gray-100'} border`}>{found?.label || status}</Badge>;
  };

  const getPaymentBadge = (status) => {
    const found = paymentStatusOptions.find(s => s.value === status);
    return <Badge className={found?.color || 'bg-gray-100'}>{found?.label || status}</Badge>;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-12 w-12 text-black animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin()) return null;

  return (
    <div className="min-h-screen py-8 bg-gray-50" data-testid="admin-rentals">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="mb-8">
            <Link to="/admin" className="inline-flex items-center text-gray-500 hover:text-black mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour au tableau de bord
            </Link>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-black">Gestion des Réservations</h1>
                  <p className="text-gray-500">{rentals.length} réservations</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-48 bg-white border-gray-300" data-testid="status-filter">
                    <SelectValue placeholder="Tous les statuts" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all">Tous</SelectItem>
                    {statusOptions.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  onClick={handleCreate}
                  className="bg-black text-white hover:bg-gray-800"
                  data-testid="add-rental-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nouvelle réservation
                </Button>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {statusOptions.map(status => {
              const count = rentals.filter(r => r.status === status.value).length;
              return (
                <Card key={status.value} className="bg-white border-gray-200">
                  <CardContent className="p-4">
                    <p className="text-gray-500 text-sm">{status.label}</p>
                    <p className="text-2xl font-bold text-black">{count}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* View Mode Tabs */}
          <Tabs value={viewMode} onValueChange={setViewMode} className="mb-6">
            <TabsList className="bg-gray-100">
              <TabsTrigger 
                value="calendar" 
                className="data-[state=active]:bg-white flex items-center gap-2"
                data-testid="calendar-view-tab"
              >
                <CalendarDays className="h-4 w-4" />
                Calendrier
              </TabsTrigger>
              <TabsTrigger 
                value="list" 
                className="data-[state=active]:bg-white flex items-center gap-2"
                data-testid="list-view-tab"
              >
                <List className="h-4 w-4" />
                Liste
              </TabsTrigger>
            </TabsList>

            {/* Calendar View */}
            <TabsContent value="calendar" className="mt-6">
              <AvailabilityCalendar
                products={products}
                reservations={rentals}
                loading={loading}
                onCreateReservation={handleCreateFromCalendar}
              />
            </TabsContent>

            {/* List View */}
            <TabsContent value="list" className="mt-6">
              <div className="space-y-4">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <div key={i} className="h-32 bg-gray-100 animate-pulse rounded-lg" />
                  ))
                ) : rentals.length === 0 ? (
                  <Card className="bg-white border-gray-200">
                    <CardContent className="py-12 text-center">
                      <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">Aucune réservation trouvée</p>
                      <Button 
                        onClick={handleCreate}
                        variant="outline"
                        className="mt-4 border-gray-300"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Créer une réservation
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  rentals.map((rental) => (
                <Card key={rental.id} className="bg-white border-gray-200" data-testid={`rental-${rental.id}`}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-semibold text-black text-lg">{rental.product_name}</h3>
                        <p className="font-mono text-gray-400 text-sm">#{rental.id.substring(0, 8)}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(rental.status)}
                        {getPaymentBadge(rental.payment_status)}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-gray-400">Client</p>
                          <p className="text-gray-700">{rental.customer_name || rental.user_name || '-'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-gray-400">Période</p>
                          <p className="text-gray-700">{formatDate(rental.start_date)} - {formatDate(rental.end_date)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-gray-400">Durée</p>
                          <p className="text-gray-700">{rental.total_days} jour(s)</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-gray-400">Total</p>
                          <p className="text-black font-mono font-semibold">{formatPrice(rental.total)}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-500">
                          Contrat: {rental.contract_signed ? (
                            <span className="text-green-600">Signé</span>
                          ) : (
                            <span className="text-yellow-600">Non signé</span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select 
                          value={rental.status} 
                          onValueChange={(v) => handleStatusChange(rental.id, v)}
                        >
                          <SelectTrigger className="w-36 h-8 bg-white border-gray-300 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-gray-200">
                            {statusOptions.map(s => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-gray-500 hover:text-black"
                          onClick={() => handleView(rental)}
                          data-testid={`view-rental-${rental.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-gray-500 hover:text-black"
                          onClick={() => handleEdit(rental)}
                          data-testid={`edit-rental-${rental.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-gray-500 hover:text-red-600"
                          onClick={() => setDeleteConfirm(rental)}
                          data-testid={`delete-rental-${rental.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-black">
              {editingRental ? 'Modifier la réservation' : 'Nouvelle réservation'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-6 py-4">
            {/* Product Selection */}
            <div className="space-y-2">
              <Label className="text-gray-700">Équipement *</Label>
              <Select 
                value={formData.product_id} 
                onValueChange={(v) => setFormData({...formData, product_id: v})}
                disabled={!!editingRental}
              >
                <SelectTrigger className="border-gray-300" data-testid="rental-product">
                  <SelectValue placeholder="Sélectionner un équipement" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-60">
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name_fr} - {formatPrice(p.rental_price_daily || p.price / 30)}/jour
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Date de début *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  className="border-gray-300"
                  data-testid="rental-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Date de fin *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  className="border-gray-300"
                  data-testid="rental-end-date"
                />
              </div>
            </div>

            {/* Customer Info */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Nom du client</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  className="border-gray-300"
                  data-testid="rental-customer-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Email</Label>
                <Input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Téléphone</Label>
                <Input
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                  className="border-gray-300"
                />
              </div>
            </div>

            {/* Status */}
            {editingRental && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Statut</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(v) => setFormData({...formData, status: v})}
                  >
                    <SelectTrigger className="border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {statusOptions.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Paiement</Label>
                  <Select 
                    value={formData.payment_status} 
                    onValueChange={(v) => setFormData({...formData, payment_status: v})}
                  >
                    <SelectTrigger className="border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {paymentStatusOptions.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Delivery Address */}
            <div className="space-y-4">
              <Label className="text-gray-700 font-medium">Adresse de livraison</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label className="text-gray-500 text-sm">Rue</Label>
                  <Input
                    value={formData.delivery_address?.street || ''}
                    onChange={(e) => setFormData({
                      ...formData, 
                      delivery_address: {...formData.delivery_address, street: e.target.value}
                    })}
                    className="border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500 text-sm">Ville</Label>
                  <Input
                    value={formData.delivery_address?.city || ''}
                    onChange={(e) => setFormData({
                      ...formData, 
                      delivery_address: {...formData.delivery_address, city: e.target.value}
                    })}
                    className="border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500 text-sm">Code postal</Label>
                  <Input
                    value={formData.delivery_address?.postal_code || ''}
                    onChange={(e) => setFormData({
                      ...formData, 
                      delivery_address: {...formData.delivery_address, postal_code: e.target.value}
                    })}
                    className="border-gray-300"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-gray-700">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="border-gray-300"
                rows={3}
                placeholder="Notes internes sur la réservation..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsModalOpen(false)}
              className="border-gray-300"
            >
              Annuler
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving || !formData.product_id || !formData.start_date || !formData.end_date}
              className="bg-black text-white hover:bg-gray-800"
              data-testid="save-rental-btn"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingRental ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-black">Détails de la réservation</DialogTitle>
          </DialogHeader>
          
          {viewingRental && (
            <div className="space-y-6 py-4">
              {/* Product & ID */}
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-semibold text-black">{viewingRental.product_name}</h3>
                  <p className="font-mono text-gray-400 text-sm">#{viewingRental.id}</p>
                </div>
                <div className="flex gap-2">
                  {getStatusBadge(viewingRental.status)}
                  {getPaymentBadge(viewingRental.payment_status)}
                </div>
              </div>

              {/* Customer Info */}
              <Card className="bg-gray-50 border-gray-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-500">Information client</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span>{viewingRental.customer_name || viewingRental.user_name || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span>{viewingRental.customer_email || viewingRental.user_email || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span>{viewingRental.customer_phone || '-'}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Dates & Pricing */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-gray-50 border-gray-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-500">Période</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-black">
                      {formatDate(viewingRental.start_date)} - {formatDate(viewingRental.end_date)}
                    </p>
                    <p className="text-gray-500 text-sm">{viewingRental.total_days} jour(s)</p>
                  </CardContent>
                </Card>
                <Card className="bg-gray-50 border-gray-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-500">Tarification</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Sous-total:</span>
                      <span>{formatPrice(viewingRental.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Taxes (TPS+TVQ):</span>
                      <span>{formatPrice(viewingRental.taxes)}</span>
                    </div>
                    <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                      <span>Total:</span>
                      <span>{formatPrice(viewingRental.total)}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Delivery Address */}
              {viewingRental.delivery_address && (
                <Card className="bg-gray-50 border-gray-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-500 flex items-center gap-2">
                      <MapPin className="h-4 w-4" /> Adresse de livraison
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p>{viewingRental.delivery_address.street}</p>
                    <p>{viewingRental.delivery_address.city}, {viewingRental.delivery_address.province} {viewingRental.delivery_address.postal_code}</p>
                  </CardContent>
                </Card>
              )}

              {/* Notes */}
              {viewingRental.notes && (
                <Card className="bg-gray-50 border-gray-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-gray-500">Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">{viewingRental.notes}</p>
                  </CardContent>
                </Card>
              )}

              {/* Contract Status */}
              <div className="flex items-center gap-2 text-sm">
                <FileText className="h-4 w-4 text-gray-400" />
                <span className="text-gray-500">Contrat:</span>
                {viewingRental.contract_signed ? (
                  <span className="text-green-600 flex items-center gap-1">
                    <Check className="h-4 w-4" /> Signé
                  </span>
                ) : (
                  <span className="text-yellow-600 flex items-center gap-1">
                    <X className="h-4 w-4" /> Non signé
                  </span>
                )}
              </div>

              {/* Important Notice */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 text-sm">
                  <strong>Politique:</strong> Paiement complet requis. Pas de remboursement. 
                  Crédit possible selon la situation (contacter le client).
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsViewModalOpen(false)}
              className="border-gray-300"
            >
              Fermer
            </Button>
            <Button 
              onClick={() => {
                setIsViewModalOpen(false);
                handleEdit(viewingRental);
              }}
              className="bg-black text-white hover:bg-gray-800"
            >
              <Edit className="h-4 w-4 mr-2" />
              Modifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-black">Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 py-4">
            Êtes-vous sûr de vouloir supprimer cette réservation pour <strong>{deleteConfirm?.product_name}</strong>? 
            Cette action est irréversible.
          </p>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteConfirm(null)}
              className="border-gray-300"
            >
              Annuler
            </Button>
            <Button 
              onClick={() => handleDelete(deleteConfirm.id)}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-rental-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
